import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import { PageLoader } from '../../shared/ui/PageLoader';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import s from './AuthCallbackPage.module.css';
import { AuthLayout } from './AuthLayout';

type State = { kind: 'waiting' } | { kind: 'signed-in' } | { kind: 'error'; message: string };

/**
 * Where Google sends the browser back to.
 *
 * This route MUST stay outside <AccountGate>. supabase-js uses PKCE, so the
 * browser lands here with a `?code=` that is exchanged for a session
 * ASYNCHRONOUSLY — there is a window where the session is still null. Inside
 * the gate, that window redirects to /login and the code is discarded, so
 * Google sign-in silently never completes and it looks like a Supabase
 * misconfiguration.
 *
 * It also must not be reachable only via the router's catch-all: that renders
 * the marketing home page, which would swallow the callback entirely.
 */
export function AuthCallbackPage(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'waiting' });

  useEffect(() => {
    let settled = false;

    // detectSessionInUrl (src/lib/supabase.ts) performs the exchange and emits
    // SIGNED_IN when it lands; subscribing is more reliable than polling.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (settled || session === null) return;
      settled = true;
      setState({ kind: 'signed-in' });
    });

    // Covers the case where the exchange already finished before we subscribed
    // (a warm session, or a fast return), and surfaces provider errors, which
    // arrive as query/hash params rather than as a failed exchange.
    void supabase.auth.getSession().then(({ data }) => {
      if (settled) return;
      if (data.session !== null) {
        settled = true;
        setState({ kind: 'signed-in' });
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const description = params.get('error_description') ?? hash.get('error_description');
      const code = params.get('error') ?? hash.get('error');
      if (description !== null || code !== null) {
        settled = true;
        setState({
          kind: 'error',
          message: description ?? `Google sign-in failed (${code ?? 'unknown error'}).`,
        });
      }
    });

    // A link that was already used, or one opened in a different browser than
    // it was started in, resolves to neither a session nor an error param — it
    // simply never completes. Without this the page would spin forever.
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setState({
        kind: 'error',
        message:
          'That sign-in link has expired or was already used. Start again from the login page.',
      });
    }, 12_000);

    return () => {
      settled = true;
      window.clearTimeout(timer);
      sub.subscription.unsubscribe();
    };
  }, []);

  if (state.kind === 'signed-in') {
    return <Navigate to="/app" replace />;
  }

  if (state.kind === 'error') {
    return (
      <AuthLayout
        pitchTitle={
          <>
            Let's try that <em>again.</em>
          </>
        }
        pitchBody="Sign-in links are single-use and expire quickly. Starting over takes a few seconds."
      >
        <h1 className={s.title}>Sign-in didn't complete</h1>
        <StatusBanner tone="error">{state.message}</StatusBanner>
        <p className={s.footer}>
          <Link to="/login">Back to login</Link>
        </p>
      </AuthLayout>
    );
  }

  return <PageLoader label="Finishing sign-in…" />;
}
